import React, { useState } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const SipSettingsTab = () => {
  const [sipConfig, setSipConfig] = useState({
    serverAddress: 'sip.callcenterpro.com.tr',
    serverPort: '5060',
    domain: 'callcenterpro.com.tr',
    transport: 'UDP',
    registrationExpiry: '3600',
    enableStun: true,
    stunServer: 'stun:stun.l.google.com:19302',
    enableTurn: false,
    turnServer: '',
    turnUsername: '',
    turnPassword: ''
  });

  const [codecSettings, setCodecSettings] = useState({
    primaryCodec: 'G.711',
    secondaryCodec: 'G.729',
    enableG711: true,
    enableG729: true,
    enableOpus: false,
    dtmfMode: 'RFC2833'
  });

  const [connectionStatus, setConnectionStatus] = useState('disconnected');

  const transportOptions = [
    { value: 'UDP', label: 'UDP' },
    { value: 'TCP', label: 'TCP' },
    { value: 'TLS', label: 'TLS' },
    { value: 'WSS', label: 'WebSocket Secure (WSS)' }
  ];

  const codecOptions = [
    { value: 'G.711', label: 'G.711 (PCMU/PCMA)' },
    { value: 'G.729', label: 'G.729' },
    { value: 'Opus', label: 'Opus' },
    { value: 'G.722', label: 'G.722' }
  ];

  const dtmfOptions = [
    { value: 'RFC2833', label: 'RFC 2833' },
    { value: 'SIP-INFO', label: 'SIP INFO' },
    { value: 'Inband', label: 'Inband' }
  ];

  const handleSipConfigChange = (field, value) => {
    setSipConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCodecChange = (field, value) => {
    setCodecSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const testConnection = () => {
    setConnectionStatus('testing');
    // Simulate connection test
    setTimeout(() => {
      setConnectionStatus('connected');
    }, 2000);
  };

  const saveSettings = () => {
    console.log('SIP ayarları kaydediliyor:', { sipConfig, codecSettings });
    // Simulate save operation
  };

  return (
    <div className="space-y-8">
      {/* Connection Status */}
      <div className="bg-white rounded-lg border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">Bağlantı Durumu</h3>
          <div className="flex items-center space-x-2">
            <div className={`w-3 h-3 rounded-full ${
              connectionStatus === 'connected' ? 'bg-success' :
              connectionStatus === 'testing' ? 'bg-warning' : 'bg-error'
            } ${connectionStatus === 'testing' ? 'pulse-slow' : ''}`} />
            <span className="text-sm font-medium text-slate-700">
              {connectionStatus === 'connected' ? 'Bağlı' :
               connectionStatus === 'testing' ? 'Test Ediliyor...' : 'Bağlantısız'}
            </span>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="text-sm text-slate-600 mb-1">Sunucu Adresi</div>
            <div className="font-medium text-slate-900">{sipConfig?.serverAddress}</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="text-sm text-slate-600 mb-1">Port</div>
            <div className="font-medium text-slate-900">{sipConfig?.serverPort}</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="text-sm text-slate-600 mb-1">Protokol</div>
            <div className="font-medium text-slate-900">{sipConfig?.transport}</div>
          </div>
        </div>

        <Button
          variant="outline"
          onClick={testConnection}
          loading={connectionStatus === 'testing'}
          iconName="Wifi"
          iconPosition="left"
        >
          Bağlantıyı Test Et
        </Button>
      </div>
      {/* SIP Server Configuration */}
      <div className="bg-white rounded-lg border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-6">SIP Sunucu Ayarları</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Sunucu Adresi"
            type="text"
            value={sipConfig?.serverAddress}
            onChange={(e) => handleSipConfigChange('serverAddress', e?.target?.value)}
            placeholder="sip.example.com"
            required
          />
          
          <Input
            label="Port"
            type="number"
            value={sipConfig?.serverPort}
            onChange={(e) => handleSipConfigChange('serverPort', e?.target?.value)}
            placeholder="5060"
            required
          />
          
          <Input
            label="Domain"
            type="text"
            value={sipConfig?.domain}
            onChange={(e) => handleSipConfigChange('domain', e?.target?.value)}
            placeholder="example.com"
            required
          />
          
          <Select
            label="Transport Protokolü"
            options={transportOptions}
            value={sipConfig?.transport}
            onChange={(value) => handleSipConfigChange('transport', value)}
          />
          
          <Input
            label="Kayıt Süresi (saniye)"
            type="number"
            value={sipConfig?.registrationExpiry}
            onChange={(e) => handleSipConfigChange('registrationExpiry', e?.target?.value)}
            placeholder="3600"
            description="Kayıt yenileme süresi"
          />
        </div>
      </div>
      {/* STUN/TURN Configuration */}
      <div className="bg-white rounded-lg border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-6">NAT Traversal Ayarları</h3>
        
        <div className="space-y-6">
          <div>
            <Checkbox
              label="STUN Sunucusunu Etkinleştir"
              description="NAT geçişi için STUN protokolünü kullan"
              checked={sipConfig?.enableStun}
              onChange={(e) => handleSipConfigChange('enableStun', e?.target?.checked)}
            />
            
            {sipConfig?.enableStun && (
              <div className="mt-4">
                <Input
                  label="STUN Sunucu Adresi"
                  type="text"
                  value={sipConfig?.stunServer}
                  onChange={(e) => handleSipConfigChange('stunServer', e?.target?.value)}
                  placeholder="stun:stun.example.com:3478"
                />
              </div>
            )}
          </div>
          
          <div>
            <Checkbox
              label="TURN Sunucusunu Etkinleştir"
              description="Güvenlik duvarı arkasındaki bağlantılar için TURN protokolünü kullan"
              checked={sipConfig?.enableTurn}
              onChange={(e) => handleSipConfigChange('enableTurn', e?.target?.checked)}
            />
            
            {sipConfig?.enableTurn && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  label="TURN Sunucu Adresi"
                  type="text"
                  value={sipConfig?.turnServer}
                  onChange={(e) => handleSipConfigChange('turnServer', e?.target?.value)}
                  placeholder="turn:turn.example.com:3478"
                />
                <Input
                  label="Kullanıcı Adı"
                  type="text"
                  value={sipConfig?.turnUsername}
                  onChange={(e) => handleSipConfigChange('turnUsername', e?.target?.value)}
                  placeholder="username"
                />
                <Input
                  label="Şifre"
                  type="password"
                  value={sipConfig?.turnPassword}
                  onChange={(e) => handleSipConfigChange('turnPassword', e?.target?.value)}
                  placeholder="password"
                />
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Codec Configuration */}
      <div className="bg-white rounded-lg border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-6">Ses Codec Ayarları</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <Select
            label="Birincil Codec"
            options={codecOptions}
            value={codecSettings?.primaryCodec}
            onChange={(value) => handleCodecChange('primaryCodec', value)}
          />
          
          <Select
            label="İkincil Codec"
            options={codecOptions}
            value={codecSettings?.secondaryCodec}
            onChange={(value) => handleCodecChange('secondaryCodec', value)}
          />
        </div>
        
        <div className="space-y-4 mb-6">
          <h4 className="font-medium text-slate-900">Desteklenen Codec'ler</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Checkbox
              label="G.711 (PCMU/PCMA)"
              description="Yüksek kalite, yüksek bant genişliği"
              checked={codecSettings?.enableG711}
              onChange={(e) => handleCodecChange('enableG711', e?.target?.checked)}
            />
            <Checkbox
              label="G.729"
              description="Düşük bant genişliği, iyi kalite"
              checked={codecSettings?.enableG729}
              onChange={(e) => handleCodecChange('enableG729', e?.target?.checked)}
            />
            <Checkbox
              label="Opus"
              description="Modern, uyarlanabilir codec"
              checked={codecSettings?.enableOpus}
              onChange={(e) => handleCodecChange('enableOpus', e?.target?.checked)}
            />
          </div>
        </div>
        
        <Select
          label="DTMF Modu"
          options={dtmfOptions}
          value={codecSettings?.dtmfMode}
          onChange={(value) => handleCodecChange('dtmfMode', value)}
          description="Tuş tonu iletim yöntemi"
        />
      </div>
      {/* Save Actions */}
      <div className="flex items-center justify-end space-x-4 pt-6 border-t border-slate-200">
        <Button variant="outline">
          Varsayılana Sıfırla
        </Button>
        <Button
          variant="default"
          onClick={saveSettings}
          iconName="Save"
          iconPosition="left"
        >
          Ayarları Kaydet
        </Button>
      </div>
    </div>
  );
};

export default SipSettingsTab;